import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { bk, nok } from "@/lib/booking";
import { ServiceCard, priceText } from "@/pages/site/Home";
import { useSeo } from "@/lib/seo";

const PriceTable = ({ services }) => (
  <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
    <table className="w-full text-sm" data-testid="price-table">
      <thead className="text-xs uppercase text-stone-500 border-b border-stone-200">
        <tr><th className="text-left p-3">Tjeneste</th><th className="text-right p-3">Pris</th><th className="text-right p-3">Minstepris</th></tr>
      </thead>
      <tbody>
        {services.map((s) => (
          <tr key={s.slug} className="border-b border-stone-100 last:border-0">
            <td className="p-3"><Link to={`/tjenester/${s.slug}`} className="font-semibold hover:text-[#2F5D50]">{s.name}</Link></td>
            <td className="p-3 text-right">{!s.bookable ? "Etter befaring" : s.unit === "vindu" ? `${nok(s.unit_price)}/vindu` : `${nok(s.price_sqm)}/m²`}</td>
            <td className="p-3 text-right">{s.bookable ? nok(s.min_price) : "–"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Services() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  useEffect(() => { bk.catalog().then(setCat); }, []);
  const s = cat?.services.find((x) => x.slug === slug);
  useSeo({ title: s ? `${s.name} – pris og bestilling | Ask-Stokke Renhold` : "Tjenester og priser for renhold | Ask-Stokke Renhold",
    description: s ? `${s.name}: ${s.description} Se pris og bestill på nett.` : "Priser per m² og minstepriser for husvask, flyttevask, visningsvask, Airbnb-vask, hovedrengjøring og vindusvask.",
    path: slug ? `/tjenester/${slug}` : undefined });
  if (!cat) return <div className="p-10 text-stone-500">Laster...</div>;
  const vatNote = cat.vat_registered ? `Priser eks. mva – ${cat.vat_rate} % mva legges til og vises før bestilling.` : "Prisene er endelige – vi er ikke mva-registrert.";

  if (!s) return (
    <main className="mx-auto max-w-6xl px-5 py-12" data-testid="services-page">
      <h1 className="font-heading text-4xl font-extrabold text-stone-900">Tjenester og priser</h1>
      <p className="mt-3 text-stone-600">Prisen er den høyeste av kvadratmeterprisen og minsteprisen, pluss eventuelle tillegg. {vatNote}</p>
      <div className="mt-8"><PriceTable services={cat.services} /></div>
      <p className="mt-3 text-xs text-stone-500">Store eller vanskelig tilgjengelige vinduer prises separat. Kontor, sameie og faste avtaler prises etter omfang – <Link to="/fast-renholdsavtale" className="underline">be om fast avtale</Link>.</p>
      <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{cat.services.map((x) => <ServiceCard key={x.slug} s={x} />)}</div>
    </main>
  );

  const ex = s.unit === "vindu" || !s.bookable ? null : [50, 80, 120].map((m) => ({ m, p: Math.max(m * s.price_sqm, s.min_price) }));
  return (
    <main className="mx-auto max-w-4xl px-5 py-12" data-testid="service-page">
      <Link to="/tjenester" className="text-sm text-stone-500 hover:text-[#2F5D50]">← Alle tjenester</Link>
      <h1 className="mt-3 font-heading text-4xl font-extrabold text-stone-900">{s.name}</h1>
      <p className="mt-4 text-lg text-stone-600">{s.description}</p>
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-stone-200 bg-white p-6">
          <div className="text-xs font-bold uppercase text-stone-500">Pris</div>
          <div className="mt-1 font-heading text-3xl font-extrabold text-[#2F5D50]">
            {priceText(s)}
          </div>
          {ex && <ul className="mt-4 space-y-1 text-sm">{ex.map((e) => <li key={e.m} className="flex justify-between"><span>{e.m} m²</span><b>{nok(e.p)}</b></li>)}</ul>}
          <p className="mt-3 text-xs text-stone-500">{vatNote}</p>
        </div>
        <div className="rounded-2xl bg-[#E7EFE9] p-6 text-sm text-stone-700 space-y-2">
          {["Vann og mikrofiber der det holder", "Milde produkter tilpasset overflaten", "Si fra om barn, kjæledyr eller egne produkter", "Fast pris før du bestiller"].map((t) => (
            <div key={t} className="flex gap-2"><Check className="h-4 w-4 text-[#2F5D50] shrink-0 mt-0.5" />{t}</div>
          ))}
        </div>
      </div>
      <Link to={s.bookable ? `/bestill?tjeneste=${s.slug}` : "/fast-renholdsavtale"} data-testid="service-book-button" className="mt-8 inline-block rounded-full bg-[#2F5D50] px-8 py-3.5 font-bold text-white hover:bg-[#244a40]">
        {s.bookable ? "Beregn pris og bestill" : "Be om fast avtale / tilbud"}
      </Link>
    </main>
  );
}
